import { sub, toPixels } from "../shared/fixed";
import type { MatchRules } from "./ability";
import { applyContact, hurtbox, worldRect } from "./combat";
import type { SimEvent } from "./events";
import { emptyShot, FIGHTER, PHASE, type MatchState } from "./state";

/**
 * Снаряды мира (магический выстрел, потом стихии): у каждого бойца свой слот. Вылетает на первом активном кадре приёма
 * со снарядом, летит по прямой, бьёт по правилам удара (блок, урон, отброс — combat.applyContact). Исчезает при
 * попадании, у стены и при встрече со снарядом соперника. Только целые числа — детерминизм отката (GAME-02).
 */
export function updateShots(state: MatchState, rules: MatchRules, events: SimEvent[]): void {
  for (const side of [0, 1] as const) spawn(state, rules, side, events);
  for (const side of [0, 1] as const) fly(state, rules, side, events);
  clash(state, events);
}

function spawn(state: MatchState, rules: MatchRules, side: 0 | 1, events: SimEvent[]): void {
  const owner = state.fighters[side];
  if (owner.state !== FIGHTER.ability || state.phase !== PHASE.fight) return;
  const ability = rules.kits[side][owner.ability];
  const spec = ability?.projectile;
  // abilityFrame уже прибавлен за этот кадр: первый активный кадр — abilityFrame − 1 === startup
  if (!ability || !spec || owner.abilityFrame - 1 !== ability.startup) return;
  const shot = state.shots[side];
  if (shot.live) return; // один снаряд за раз: второй выстрел — только когда первый погас
  shot.live = 1;
  shot.x = owner.x + sub(spec.from.x) * owner.facing;
  shot.y = owner.y + sub(spec.from.y);
  shot.vx = sub(spec.speed) * owner.facing;
  shot.ability = owner.ability;
  shot.age = 0;
  events.push({
    kind: "action",
    frame: state.frame,
    who: side,
    x: toPixels(shot.x),
    y: toPixels(shot.y),
    strength: 1,
    ...(ability.fx?.start ? { fx: ability.fx.start } : {}),
  });
}

function fly(state: MatchState, rules: MatchRules, side: 0 | 1, events: SimEvent[]): void {
  const shot = state.shots[side];
  if (!shot.live) return;
  const ability = rules.kits[side][shot.ability];
  const spec = ability?.projectile;
  if (!ability?.hit || !spec) {
    state.shots[side] = emptyShot();
    return;
  }
  shot.x += shot.vx;
  shot.age++;
  const { arena } = rules;
  if (shot.x < sub(arena.left) || shot.x > sub(arena.right)) {
    fizzle(state, side, events);
    return;
  }
  const defenderSide: 0 | 1 = side === 0 ? 1 : 0;
  const defender = state.fighters[defenderSide];
  const hurt = hurtbox(defender, rules, defenderSide);
  if (!hurt || state.phase !== PHASE.fight) return;
  const body = worldRect(defender, hurt);
  const box = shotRect(shot, spec.box);
  if (box.right < body.left || box.left > body.right || box.bottom < body.top || box.top > body.bottom) return;
  applyContact(
    state,
    rules,
    {
      attacker: side,
      ability,
      hit: ability.hit,
      x: toPixels(shot.x),
      y: toPixels(shot.y),
      direction: shot.vx >= 0 ? 1 : -1,
      shot: true,
    },
    events,
  );
  state.shots[side] = emptyShot();
}

/** Два снаряда встретились — гаснут оба, вспышка посередине. */
function clash(state: MatchState, events: SimEvent[]): void {
  const [a, b] = state.shots;
  if (!a.live || !b.live) return;
  const gap = Math.abs(a.x - b.x);
  if (gap > sub(24) || Math.abs(a.y - b.y) > sub(30)) return;
  events.push({
    kind: "action",
    frame: state.frame,
    who: 0,
    x: toPixels((a.x + b.x) / 2),
    y: toPixels((a.y + b.y) / 2),
    strength: 2,
    fx: "magic/clash",
  });
  state.shots = [emptyShot(), emptyShot()];
}

function fizzle(state: MatchState, side: 0 | 1, events: SimEvent[]): void {
  const shot = state.shots[side];
  events.push({
    kind: "action",
    frame: state.frame,
    who: side,
    x: toPixels(shot.x),
    y: toPixels(shot.y),
    strength: 0,
    fx: "magic/fizzle",
  });
  state.shots[side] = emptyShot();
}

function shotRect(
  shot: { x: number; y: number },
  box: { x: number; y: number; w: number; h: number },
): { left: number; right: number; top: number; bottom: number } {
  const left = shot.x + sub(box.x);
  const top = shot.y + sub(box.y);
  return { left, right: left + sub(box.w), top, bottom: top + sub(box.h) };
}
